/*
 * hs-ajax-search.js
 * ---------------------------------------------------------------------------
 * Shared AJAX (predictive) search for HS Main Header, HS Wholesale Header and
 * the mobile sidebar. Matches on product name AND variant SKU, renders results
 * in a dropdown under the input and never leaves the current page unless the
 * shopper picks a result or hits Enter.
 *
 * Markup contract (see the header sections):
 *   <div data-hs-search>
 *     <form data-hs-search-form action="/search" method="get"> ... <input type="search" name="q"> </form>
 *     <div data-hs-search-panel hidden></div>
 *   </div>
 *
 * Results HTML comes from sections/hs-predictive-search.liquid via the
 * Predictive Search API + Section Rendering API. "variants.sku" has to be in
 * the fields list — the API's default set is title, product_type,
 * variants.title and vendor only.
 * Docs: https://shopify.dev/docs/api/ajax/reference/predictive-search
 */
(function () {
  if (window.hsAjaxSearchLoaded) return;
  window.hsAjaxSearchLoaded = true;

  var SECTION_ID = 'hs-predictive-search';
  var FIELDS = 'title,product_type,variants.title,variants.sku,vendor';
  var LIMIT = 8;
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;

  var routes = (window.Shopify && window.Shopify.routes) || {};
  var root = routes.root || '/';
  var predictiveUrl = routes.predictive_search_url || root.replace(/\/$/, '') + '/search/suggest';

  var cache = {};
  var timers = new WeakMap();
  var controllers = new WeakMap();

  function panelOf(wrap) {
    return wrap.querySelector('[data-hs-search-panel]');
  }

  function inputOf(wrap) {
    return wrap.querySelector('input[type="search"], input[name="q"]');
  }

  function close(wrap) {
    var panel = panelOf(wrap);
    if (!panel) return;
    panel.hidden = true;
    panel.innerHTML = '';
    wrap.classList.remove('hs-search--open');
    var input = inputOf(wrap);
    if (input) input.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    document.querySelectorAll('[data-hs-search]').forEach(function (wrap) {
      if (wrap !== except) close(wrap);
    });
  }

  function open(wrap, html) {
    var panel = panelOf(wrap);
    if (!panel) return;
    panel.innerHTML = html;
    panel.hidden = false;
    wrap.classList.add('hs-search--open');
    var input = inputOf(wrap);
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function loading(wrap) {
    open(wrap, '<div class="hs-search-panel__loading">Searching…</div>');
  }

  function buildUrl(term) {
    return (
      predictiveUrl +
      '?q=' + encodeURIComponent(term) +
      '&section_id=' + SECTION_ID +
      '&resources[type]=product' +
      '&resources[limit]=' + LIMIT +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + encodeURIComponent(FIELDS)
    );
  }

  function search(wrap, term) {
    var key = term.toLowerCase();

    if (cache[key]) {
      open(wrap, cache[key]);
      return;
    }

    var previous = controllers.get(wrap);
    if (previous) previous.abort();

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) controllers.set(wrap, controller);

    loading(wrap);

    fetch(buildUrl(term), controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        cache[key] = html;
        if (inputOf(wrap) && inputOf(wrap).value.trim().length >= MIN_CHARS) open(wrap, html);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // Silent fallback: the form still submits to the full search page.
        close(wrap);
      });
  }

  function onInput(wrap) {
    var input = inputOf(wrap);
    if (!input) return;

    var term = input.value.trim();

    clearTimeout(timers.get(wrap));

    if (term.length < MIN_CHARS) {
      close(wrap);
      return;
    }

    timers.set(
      wrap,
      setTimeout(function () {
        search(wrap, term);
      }, DEBOUNCE_MS)
    );
  }

  function items(wrap) {
    var panel = panelOf(wrap);
    return panel ? Array.prototype.slice.call(panel.querySelectorAll('.hs-ps__item')) : [];
  }

  function move(wrap, direction) {
    var list = items(wrap);
    if (!list.length) return;

    var current = list.findIndex(function (li) {
      return li.getAttribute('aria-selected') === 'true';
    });
    var next = current + direction;

    if (next < 0) next = list.length - 1;
    if (next >= list.length) next = 0;

    list.forEach(function (li, index) {
      li.setAttribute('aria-selected', index === next ? 'true' : 'false');
    });
    list[next].scrollIntoView({ block: 'nearest' });
  }

  function activeLink(wrap) {
    var selected = items(wrap).find(function (li) {
      return li.getAttribute('aria-selected') === 'true';
    });
    return selected ? selected.querySelector('a') : null;
  }

  document.addEventListener('input', function (event) {
    var wrap = event.target.closest && event.target.closest('[data-hs-search]');
    if (!wrap) return;
    if (!event.target.matches('input[type="search"], input[name="q"]')) return;
    onInput(wrap);
  });

  document.addEventListener('focusin', function (event) {
    var wrap = event.target.closest && event.target.closest('[data-hs-search]');
    closeAll(wrap || null);
    if (!wrap) return;
    if (event.target.matches('input[type="search"], input[name="q"]')) onInput(wrap);
  });

  document.addEventListener('keydown', function (event) {
    var wrap = event.target.closest && event.target.closest('[data-hs-search]');
    if (!wrap) return;

    if (event.key === 'Escape') {
      close(wrap);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!items(wrap).length) return;
      event.preventDefault();
      move(wrap, event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter') {
      var link = activeLink(wrap);
      if (link) {
        event.preventDefault();
        window.location.href = link.href;
      }
      // No highlighted row: let the form submit to the full search page.
    }
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('[data-hs-search]')) return;
    closeAll(null);
  });

  document.addEventListener('submit', function (event) {
    var wrap = event.target.closest && event.target.closest('[data-hs-search]');
    if (!wrap) return;
    var input = inputOf(wrap);
    if (input && input.value.trim() === '') event.preventDefault();
  });
})();
